import i18n from '../i18n'
import { localizeApiDetail } from '../i18n/apiError'

// Authentication service for JWT-based auth
class AuthService {
  private accessToken: string | null = null
  private tokenExpiresAt: number | null = null
  private refreshPromise: Promise<any> | null = null
  // Use environment variable for production, fallback to relative path for development
  private baseURL: string = import.meta.env.VITE_API_BASE_URL || '/api/v1'

  // Store access token in memory (NOT localStorage for security)
  setAccessToken(token: string, expiresIn: number) {
    this.accessToken = token
    this.tokenExpiresAt = Date.now() + (expiresIn * 1000)
  }

  getAccessToken(): string | null {
    return this.accessToken
  }

  isTokenExpired(): boolean {
    if (!this.tokenExpiresAt) return true
    // Consider token expired 30 seconds before actual expiry
    return Date.now() >= (this.tokenExpiresAt - 30000)
  }

  clearTokens() {
    this.accessToken = null
    this.tokenExpiresAt = null
  }

  // Register new user
  async register(email: string, password: string, username: string | null = null, announcement_id?: number, contact_phone?: string, company_name?: string) {
    const body: any = { email, password, username }
    if (announcement_id !== undefined) {
      body.announcement_id = announcement_id
    }
    if (contact_phone !== undefined) {
      body.contact_phone = contact_phone
    }
    if (company_name !== undefined) {
      body.company_name = company_name
    }

    const response = await fetch(`${this.baseURL}/auth/jwt/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(localizeApiDetail(error.detail) || i18n.t('authService.registrationFailed'))
    }

    return await response.json()
  }

  // Login
  async login(email: string, password: string) {
    const response = await fetch(`${this.baseURL}/auth/jwt/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(localizeApiDetail(error.detail) || i18n.t('authService.loginFailed'))
    }

    const data = await response.json()
    console.log('Login successful, token expires in:', data.expires_in, 'seconds')
    console.log('Cookie headers:', response.headers.get('set-cookie'))
    this.setAccessToken(data.access_token, data.expires_in)
    return data
  }

  // Refresh access token
  async refreshAccessToken() {
    // Prevent multiple simultaneous refresh calls
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = fetch(`${this.baseURL}/auth/jwt/refresh`, {
      method: 'POST',
      credentials: 'include'
    })
      .then(async (response) => {
        if (!response.ok) {
          this.clearTokens()
          throw new Error(i18n.t('authService.refreshFailed'))
        }

        const data = await response.json()
        this.setAccessToken(data.access_token, data.expires_in)
        return data
      })
      .finally(() => {
        this.refreshPromise = null
      })

    return this.refreshPromise
  }

  // Logout
  async logout() {
    try {
      await fetch(`${this.baseURL}/auth/jwt/logout`, {
        method: 'POST',
        credentials: 'include'
      })
    } finally {
      this.clearTokens()
    }
  }

  // Get current user
  async getCurrentUser() {
    const response = await this.fetchWithAuth(`${this.baseURL}/auth/jwt/me`)
    
    if (!response.ok) {
      throw new Error(i18n.t('authService.fetchUserFailed'))
    }

    return await response.json()
  }

  // Change password (Note: Backend endpoint not yet documented)
  async changePassword(currentPassword: string, newPassword: string) {
    // TODO: Update this endpoint path when backend implements change-password
    const response = await this.fetchWithAuth(`${this.baseURL}/auth/jwt/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        current_password: currentPassword, 
        new_password: newPassword 
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(localizeApiDetail(error.detail) || i18n.t('authService.passwordChangeFailed'))
    }

    return await response.json()
  }

  // Make authenticated API calls
  async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    // If no token at all, don't even try
    if (!this.accessToken) {
      throw new Error(i18n.t('authService.noAccessToken'))
    }

    // Check if token needs refresh
    if (this.isTokenExpired()) {
      try {
        await this.refreshAccessToken()
      } catch (error) {
        // Redirect to login if refresh fails
        this.clearTokens()
        throw error
      }
    }

    // Add Authorization header
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${this.getAccessToken()}`
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    })

    // Handle 401 - try to refresh token once
    if (response.status === 401 && !(options as any)._retry) {
      try {
        await this.refreshAccessToken()
        
        // Retry the original request with new token
        headers['Authorization'] = `Bearer ${this.getAccessToken()}`
        const retryOptions = {
          ...options,
          headers,
          credentials: 'include' as RequestCredentials,
          _retry: true
        }
        return await fetch(url, retryOptions)
      } catch (error) {
        this.clearTokens()
        throw error
      }
    }

    return response
  }

  // Password Reset Methods

  // Request password reset email
  async forgotPassword(email: string) {
    const response = await fetch(`${this.baseURL}/auth/jwt/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(localizeApiDetail(error.detail) || i18n.t('authService.forgotFailed'))
    }

    return await response.json()
  }

  // Validate reset token
  async validateResetToken(token: string) {
    const response = await fetch(`${this.baseURL}/auth/jwt/validate-reset-token?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include'
    })

    if (!response.ok) {
      return { valid: false, message: i18n.t('authService.invalidToken') }
    }

    return await response.json()
  }

  // Reset password with token
  async resetPassword(token: string, newPassword: string) {
    const response = await fetch(`${this.baseURL}/auth/jwt/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token, new_password: newPassword })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(localizeApiDetail(error.detail) || i18n.t('authService.resetFailed'))
    }

    return await response.json()
  }
}

export const authService = new AuthService()
